/**
 * server-api.js
 *
 * Client for the VPS Express server's pre-computed endpoints.
 * Only available when REACT_APP_API_MODE=vps.
 *
 * These endpoints serve data that was pre-fetched and pre-computed
 * server-side on an hourly schedule, so page loads go from ~10 seconds
 * of kline fetching down to a single ~50ms API call.
 */

import { getServerApiBase } from './api-config';

const BASE = getServerApiBase();

/**
 * Returns true when VPS server endpoints are available.
 */
export function isServerApiAvailable() {
  return BASE !== null;
}

async function serverFetch(path) {
  if (!BASE && BASE !== '') throw new Error('Server API not available in Netlify mode');
  const url = `${BASE}${path}`;
  const res = await fetch(url, { signal: AbortSignal.timeout(10000) });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Server API error ${res.status}`);
  }
  return res.json();
}

/**
 * Fetch pre-computed market data (coins + indicators) for the dashboard.
 *
 * Returns:
 *   {
 *     coins: [{ id, name, symbol, image, current_price, market_cap_rank, ...,
 *               indicators: { weekly, daily, fourHour } }],
 *     lastUpdated: ISO string
 *   }
 */
export async function fetchServerMarketData() {
  return serverFetch('/api/market-data');
}

/**
 * Fetch pre-computed rubberband states for the Rubberband page.
 *
 * Returns:
 *   {
 *     rubberband: {
 *       weekly:   [{ id, symbol, name, image, currentPrice, state, sma, slope, pctFromSMA, ... }],
 *       daily:    [...],
 *       fourHour: [...],
 *     },
 *     lastUpdated: ISO string
 *   }
 */
export async function fetchServerRubberbandData() {
  return serverFetch('/api/rubberband');
}

/**
 * Fetch pre-computed detail for a single coin (indicators + 30d price history).
 *
 * @param {string} coinId  CoinGecko ID, e.g. "bitcoin"
 * Returns:
 *   {
 *     coin: { ...CoinGecko market fields },
 *     indicators: { weekly, daily, fourHour },
 *     priceHistory: [[timestamp, price], ...]
 *   }
 */
export async function fetchServerCoinDetail(coinId) {
  return serverFetch(`/api/coin/${encodeURIComponent(coinId)}`);
}

/**
 * Fetch a coin's project description and latest news for the
 * "About & News" section at the bottom of the coin detail page.
 *
 * @param {string} coinId  CoinGecko ID, e.g. "bitcoin"
 * Returns:
 *   {
 *     description: string|null,   // CoinGecko project description (English, may contain HTML)
 *     homepage: string|null,
 *     symbol: string|null,
 *     news: [{ title, url, source, imageUrl, publishedAt }]
 *   }
 */
export async function fetchCoinInfo(coinId) {
  return serverFetch(`/api/coin-info/${encodeURIComponent(coinId)}`);
}

/**
 * Fetch server health / cache status.
 *
 * Returns:
 *   {
 *     status: 'ok' | 'initialising',
 *     lastUpdated: ISO string | null,
 *     isRefreshing: boolean,
 *     coinCount: number,
 *     rubberband: { weekly, daily, fourHour },
 *     error: string | null
 *   }
 */
export async function fetchServerStatus() {
  return serverFetch('/api/status');
}

/**
 * Fetch the historical alerts log.
 * Returns: { log: [{ id, date, coinId, symbol, name, image, strategy, direction,
 *                    entryPrice, indicators, exitPrice, percentGain, status }, ...] }
 */
export async function fetchAlertsLog() {
  return serverFetch('/api/alerts-log');
}

/**
 * Fetch pre-computed stocks market data (stocks + indicators).
 * Returns: { stocks: [...], lastUpdated }
 */
export async function fetchStocksMarketData() {
  return serverFetch('/api/stocks/market-data');
}

/**
 * Fetch Bittensor (TAO) subnet market data (Phase 1 — price/market cap/change/
 * volume, no indicators yet).
 * Returns: { subnets: [...], lastUpdated }
 */
export async function fetchTaoSubnetsData() {
  return serverFetch('/api/tao-subnets');
}

/**
 * Fetch pre-computed rubberband states for stocks.
 * Returns: { rubberband: { daily, fourHour }, lastUpdated }
 */
export async function fetchStocksRubberbandData() {
  return serverFetch('/api/stocks/rubberband');
}

/** Fetch CCI Pullback signals. Returns: { cciPullback: { daily, fourHour }, lastUpdated } */
export async function fetchCciPullbackData() {
  return serverFetch('/api/cci-pullback');
}

/** Fetch MTF 50 SMA signals. Returns: { mtf50sma: { daily, fourHour }, lastUpdated } */
export async function fetchMtf50smaData() {
  return serverFetch('/api/mtf-50sma');
}

/** Fetch Trend Pullback signals. Returns: { trendPullback: { weekly, daily, fourHour }, lastUpdated } */
export async function fetchTrendPullbackData() {
  return serverFetch('/api/trend-pullback');
}

/** Fetch TopDog signals. Returns: { topDog: { weekly, daily, fourHour }, lastUpdated } */
export async function fetchTopDogData() {
  return serverFetch('/api/topdog');
}

/** Fetch TopDog + CCI Pullback combined signals. Returns: { topDogCciCombined: { daily, fourHour }, lastUpdated } */
export async function fetchTopDogCciCombinedData() {
  return serverFetch('/api/topdog-cci-combined');
}

/** Fetch 20-50 EMA Pullback signals. Returns: { twentyFiftyPullback: { weekly, daily, fourHour }, lastUpdated } */
export async function fetchTwentyFiftyPullbackData() {
  return serverFetch('/api/20-50-pullback');
}

/** Fetch 20 EMA S/R signals. Returns: { twentyEmaSr: { weekly, daily, fourHour }, lastUpdated } */
export async function fetchTwentyEmaSrData() {
  return serverFetch('/api/20ema-sr');
}

/** Fetch 20/8 EMA Parabolic signals. Returns: { emaParabolic: { weekly, daily, fourHour }, lastUpdated } */
export async function fetchEmaParabolicData() {
  return serverFetch('/api/20-8ema-parabolic');
}

/** Fetch Volume Spike signals. Returns: { volumeSpike: { current, sustained }, lastUpdated } */
export async function fetchVolumeSpikeData() {
  return serverFetch('/api/volume-spike');
}

/** Fetch Trend Breakout signals. Returns: { trendBreakout: { weekly, daily, fourHour }, lastUpdated } */
export async function fetchTrendBreakoutData() {
  return serverFetch('/api/trend-breakout');
}

/** Fetch TTM Squeeze signals. Returns: { ttmSqueeze: { weekly, daily, fourHour }, lastUpdated } */
export async function fetchTtmSqueezeData() {
  return serverFetch('/api/ttm-squeeze');
}

/** Fetch KC Extreme signals. Returns: { kcExtreme: { daily, fourHour }, lastUpdated } */
export async function fetchKcExtremeData() {
  return serverFetch('/api/kc-extreme');
}

/** Fetch MA Crossover signals. Returns: { maCrossover: { weekly, daily, fourHour }, lastUpdated } */
export async function fetchMaCrossoverData() {
  return serverFetch('/api/ma-crossover');
}

/** Fetch coins in a Weekly + Daily + 4H TTM squeeze at once. Returns: { tripleSqueeze: [...], lastUpdated } */
export async function fetchTtmTripleSqueezeData() {
  return serverFetch('/api/ttm-triple-squeeze');
}

/** Fetch Combined (multi-strategy confluence) signals. Returns: { combined: { weekly, daily, fourHour }, lastUpdated } */
export async function fetchCombinedData() {
  return serverFetch('/api/combined');
}

/**
 * Update an alert log entry with an exit price (marks it as closed).
 * @param {string} entryId  UUID of the log entry
 * @param {object} updates  { exitPrice?: number, status?: string }
 */
export async function updateAlertLogEntry(entryId, updates) {
  if (!BASE && BASE !== '') throw new Error('Server API not available');
  const res = await fetch(`${BASE}/api/alerts-log/${encodeURIComponent(entryId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
    signal: AbortSignal.timeout(10000),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Server API error ${res.status}`);
  }
  return res.json();
}
